import React from 'react';
import PropTypes from 'prop-types';
import { Badge } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import {
  getBoxSizeOpportunities,
  isUpsCarrier,
} from '../../utilities/boxSizeOpportunities';

function getOpportunityData(dims, weight, carrier, service) {
  if (typeof dims !== 'string' || !isUpsCarrier(carrier)) return null;

  const numericDimensions = dims
    .toLowerCase()
    .replace(/\s/g, '')
    .split('x')
    .map(Number);
  const numericWeight = Number(weight);

  if (
    numericDimensions.length !== 3
    || numericDimensions.some((dimension) => !Number.isFinite(dimension) || dimension <= 0)
    || !Number.isFinite(numericWeight)
    || numericWeight <= 0
  ) {
    return null;
  }

  return getBoxSizeOpportunities(dims, numericWeight, { carrier, service });
}

function getImpact(opportunity, t) {
  if (opportunity.benefits.some((benefit) => benefit.type === 'AVOIDS_LARGE_PACKAGE')) {
    return { color: 'danger', label: t('High impact') };
  }

  if (opportunity.benefits.some((benefit) => benefit.type === 'AVOIDS_ADDITIONAL_HANDLING')) {
    return { color: 'warning', label: t('Surcharge opportunity') };
  }

  if (opportunity.benefits.some((benefit) => benefit.type === 'SUREPOST_DIM_EXEMPT')) {
    return { color: 'warning', label: t('SurePost opportunity') };
  }

  return { color: 'info', label: t('DIM weight opportunity') };
}

function renderBenefit(benefit, t) {
  switch (benefit.type) {
    case 'AVOIDS_LARGE_PACKAGE':
      return t('Avoids UPS Large Package status');
    case 'AVOIDS_ADDITIONAL_HANDLING':
      return t('Avoids UPS Additional Handling');
    case 'SUREPOST_DIM_EXEMPT':
      return t('Reaches the SurePost DIM-exempt size limit');
    case 'DIM_WEIGHT':
      return `${t('Reduces billable weight')} ${benefit.from} → ${benefit.to} ${t('lb')}`;
    default:
      return null;
  }
}

function BoxDimsOpportunity({
  dims, weight, carrier, service,
}) {
  const { t } = useTranslation();
  const data = getOpportunityData(dims, weight, carrier, service);
  const opportunity = data?.bestOpportunity;

  if (!opportunity) return null;

  const impact = getImpact(opportunity, t);

  return (
    <div className={`box-dims-opportunity box-dims-opportunity--${impact.color}`} role="status">
      <div className="box-dims-opportunity__header">
        <div className="box-dims-opportunity__title">
          <span className="mdi mdi-package-variant-closed-check me-1" aria-hidden="true" />
          {t('Box dims opportunity')}
        </div>
        <Badge color={impact.color} pill>{impact.label}</Badge>
      </div>
      <div className="box-dims-opportunity__dimensions">
        {opportunity.from.split('x').join('×')}
        <span className="mdi mdi-arrow-right mx-2" aria-hidden="true" />
        {opportunity.to.split('x').join('×')}
      </div>
      <div className="box-dims-opportunity__prompt">
        {t('A one-inch reduction on this side may lower the shipping charge.')}
      </div>
      <ul className="box-dims-opportunity__benefits">
        {opportunity.benefits.map((benefit) => (
          <li key={benefit.type}>{renderBenefit(benefit, t)}</li>
        ))}
      </ul>
    </div>
  );
}

BoxDimsOpportunity.propTypes = {
  dims: PropTypes.string.isRequired,
  weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  carrier: PropTypes.string,
  service: PropTypes.string,
};

BoxDimsOpportunity.defaultProps = {
  carrier: '',
  service: '',
};

export default BoxDimsOpportunity;
